import React, { createContext, useContext, ReactNode, useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { TimelineEntry, BucketItem } from '../types';
import { getTimelineWithCounts } from '../utils/dummyData';
import { supabase } from '../utils/supabaseClient';
import { User } from '@supabase/supabase-js';

/* 
 * [Supabase Sync Strategy]
 * 1. profiles: link user to couple_id
 * 2. couples: store shared info (names, dates)
 * 3. timeline_events: shared memories (filter by couple_id)
 * 4. bucket_list: shared goals (filter by couple_id)
 */

interface CoupleInfo {
  name1: string;
  name2: string;
  anniversaryDate: string;
  firstMetDate: string;
  baseDateType: 'met' | 'dated';
}

interface AppContextType {
  user: User | null;
  coupleId: string | null;
  signOut: () => Promise<void>;
  linkCouple: (partnerCode: string) => Promise<void>;
  coupleInfo: CoupleInfo;
  setCoupleInfo: (info: CoupleInfo) => Promise<void>;
  entries: TimelineEntry[];
  addEntry: (entry: Omit<TimelineEntry, 'id' | 'meetingCount'>) => Promise<void>;
  updateEntry: (id: string, entry: Partial<TimelineEntry>) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  entriesWithCounts: TimelineEntry[];
  bucketItems: BucketItem[];
  addBucketItem: (item: Omit<BucketItem, 'id'>) => Promise<void>;
  updateBucketItem: (id: string, item: Partial<BucketItem>) => Promise<void>;
  toggleBucketItem: (id: string) => Promise<void>;
  isLoading: boolean;
  loadingMessage: string;
  initError: string | null;
  dDayMode: 'met' | 'dated';
  setDDayMode: (mode: 'met' | 'dated') => void;
  withdraw: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [coupleId, setCoupleId] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('정보를 불러오고 있습니다...');
  const [isLoading, setIsLoading] = useState(true);
  
  const lastUserId = useRef<string | null>(null);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local cache for coupleInfo
  const [coupleInfo, setCoupleInfoState] = useLocalStorage<CoupleInfo>('couple_info', {
    name1: '나',
    name2: '상대방',
    anniversaryDate: '2024-12-20',
    firstMetDate: '2024-11-15',
    baseDateType: 'dated',
  });

  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [bucketItems, setBucketItems] = useState<BucketItem[]>([]);
  
  const [dDayMode, setDDayModeState] = useState<'met' | 'dated'>(() => {
    const saved = localStorage.getItem('dDayMode');
    return (saved as 'met' | 'dated') || 'dated';
  });

  const setDDayMode = (mode: 'met' | 'dated') => {
    setDDayModeState(mode);
    localStorage.setItem('dDayMode', mode);
  };

  const fetchCoupleData = useCallback(async (cId: string, userId?: string) => {
    console.log(`[Fetch] Couple data start: ${cId}`);
    try {
      // Cache invalidation: Add a unique filter to force fresh data from server (Query Param effect)
      const cacheBuster = `00000000-0000-4000-a000-${Date.now().toString().slice(-12)}`;
      
      const { data: coupleData, error: coupleError } = await supabase
        .from('couples')
        .select('*')
        .eq('id', cId)
        .filter('id', 'not.eq', cacheBuster)
        .limit(1);

      if (coupleError) throw coupleError;
      
      let finalInfo = {
        name1: '나',
        name2: '상대방',
        anniversaryDate: '2024-12-20',
        firstMetDate: '2024-11-15',
        baseDateType: 'dated' as 'met' | 'dated',
      };

      if (coupleData && coupleData.length > 0) {
        const record = coupleData[0];
        finalInfo = {
          name1: record.name1,
          name2: record.name2,
          anniversaryDate: record.anniversary_date,
          firstMetDate: record.first_met_date,
          baseDateType: record.base_date_type,
        };
        console.log('[Fetch] Couple data loaded from shared space');
      }

      // 1순위/2순위 우선순위 적용: 혼자인 경우(cId === userId) profiles의 manual_ 데이터 우선 참조
      if (userId && (cId === userId)) {
        console.log('[Fetch] Checking manual profile data as priority for solo mode...');
        // Use a dummy filter with timestamp to bypass potential intermediate caches
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('manual_partner_name, manual_first_met_date, manual_start_date')
          .eq('id', userId)
          .filter('id', 'not.eq', cacheBuster)
          .single();

        if (profileError) {
          console.error('[Fetch] Profile manual data check error:', profileError);
        } else if (profileData) {
          if (profileData.manual_partner_name) finalInfo.name2 = profileData.manual_partner_name;
          if (profileData.manual_first_met_date) finalInfo.firstMetDate = profileData.manual_first_met_date;
          if (profileData.manual_start_date) finalInfo.anniversaryDate = profileData.manual_start_date;
          console.log('[Fetch] Applied manual profile data (Priority 2 over shared)');
        }
      }

      setCoupleInfoState(finalInfo);
    } catch (error) {
      console.error('[Error] fetchCoupleData:', error);
    }
  }, [setCoupleInfoState]);

  const fetchEntries = useCallback(async (cId: string | null, userId: string) => {
    console.log(`[Fetch] Entries start (cId: ${cId}, userId: ${userId})`);
    try {
      let query = supabase.from('timeline_events').select('*');
      
      // Filter logic:
      // 1. If couple-linked (cId !== userId), show everything in that shared space
      // 2. Also always show anything created by ME (created_by)
      // 3. If solo, also show entries where couple_id is null AND created_by is ME
      if (cId && cId !== userId) {
        query = query.or(`couple_id.eq.${cId},created_by.eq.${userId}`);
      } else {
        // Solo mode or same ID
        query = query.or(`created_by.eq.${userId},couple_id.eq.${userId},couple_id.is.null`);
      }

      const { data, error } = await query.order('event_date', { ascending: true });
      if (error) throw error;
      setEntries(data || []);
      console.log(`[Fetch] Entries success: ${data?.length || 0} items`);
    } catch (error) {
      console.error('[Error] fetchEntries:', error);
    }
  }, []);

  const fetchBucketItems = useCallback(async (cId: string | null, userId: string) => {
    console.log(`[Fetch] Bucket items start (cId: ${cId}, userId: ${userId})`);
    try {
      let query = supabase.from('bucket_list').select('*');
      if (cId && cId !== userId) {
        query = query.or(`couple_id.eq.${cId},created_by.eq.${userId}`);
      } else {
        query = query.or(`created_by.eq.${userId},couple_id.eq.${userId},couple_id.is.null`);
      }

      const { data, error } = await query;
      if (error) throw error;
      setBucketItems(data?.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description,
        isCompleted: item.is_completed,
        category: item.category,
        targetDate: item.target_date
      })) || []);
      console.log(`[Fetch] Bucket items success: ${data?.length || 0} items`);
    } catch (error) {
      console.error('[Error] fetchBucketItems:', error);
    }
  }, []);

  const setupData = useCallback(async (cId: string | null, userId: string) => {
    console.log('[Setup] Running parallel fetch tasks...');
    const tasks = [];
    if (cId) tasks.push(fetchCoupleData(cId, userId));
    tasks.push(fetchEntries(cId, userId));
    tasks.push(fetchBucketItems(cId, userId));
    await Promise.all(tasks);
    console.log('[Setup] All fetch tasks finished');
  }, [fetchCoupleData, fetchEntries, fetchBucketItems]);

  const setupUser = useCallback(async (currentUser: User | null) => {
    const currentId = currentUser?.id || null;
    if (lastUserId.current === currentId && currentId !== null) {
      console.log('[Auth] Ignoring redundant setup for same user ID');
      return;
    }
    lastUserId.current = currentId;

    console.log('[Auth] Starting setup sequence for:', currentUser ? currentUser.email : 'Signed out');
    setUser(currentUser);
    setInitError(null);
    
    if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);

    if (currentUser) {
      setIsLoading(true);
      setLoadingMessage('사용자 정보를 확인하고 있습니다...');

      initTimeoutRef.current = setTimeout(() => {
        setInitError('서버 응답 시간이 초과되었습니다. 페이지를 새로고침하거나 다시 로그인해 주세요.');
        setIsLoading(false);
      }, 20000);

      try {
        console.log('[Profile] Fetching profile...');
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('couple_id')
          .eq('id', currentUser.id)
          .limit(1);

        if (profileError) throw new Error(`프로필 조회 실패: ${profileError.message}`);

        let currentCoupleId: string | null = null;

        if (!profiles || profiles.length === 0) {
          console.log('[Profile] No profile found, creating...');
          setLoadingMessage('정보를 설정 중입니다...');
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .upsert([{ id: currentUser.id, couple_id: currentUser.id }], { onConflict: 'id' })
            .select()
            .limit(1);
          
          if (createError) throw new Error(`프로필 생성 실패: ${createError.message}`);
          currentCoupleId = newProfile?.[0]?.couple_id || currentUser.id;
          console.log('[Profile] Created new profile');
        } else {
          currentCoupleId = profiles[0].couple_id;
          console.log('[Profile] Found existing profile');
        }

        setCoupleId(currentCoupleId);
        
        if (currentCoupleId) {
          const cId = currentCoupleId;
          const uid = currentUser.id;

          console.log('[Couple] Verifying shared space...');
          const { data: couples, error: coupleCheckError } = await supabase
            .from('couples')
            .select('id')
            .eq('id', cId)
            .limit(1);

          if (coupleCheckError) console.error('[Couple] Check error:', coupleCheckError);

          if (!couples || couples.length === 0) {
            console.log('[Couple] Shared space not found. Initializing with minimal defaults...');
            setLoadingMessage('커플 공간을 준비 중입니다...');
            const { error: coupleInitError } = await supabase.from('couples').upsert([{
              id: cId,
              name1: '나',
              name2: '상대방',
              anniversary_date: '2024-12-20',
              first_met_date: '2024-11-15',
              base_date_type: 'dated'
            }], { onConflict: 'id' });

            if (coupleInitError) throw new Error(`커플 공간 생성 실패: ${coupleInitError.message}`);
            console.log('[Couple] Shared space initialized');
          } else {
            console.log('[Couple] Shared space verified');
          }

          setLoadingMessage('데이터를 동기화 중입니다...');
          await setupData(cId, uid);

          // Realtime setup
          console.log('[Realtime] Connecting...');
          supabase.channel(`sync-${cId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'timeline_events', filter: `couple_id=eq.${cId}` }, () => fetchEntries(cId, uid))
            .on('postgres_changes', { event: '*', schema: 'public', table: 'bucket_list', filter: `couple_id=eq.${cId}` }, () => fetchBucketItems(cId, uid))
            .on('postgres_changes', { event: '*', schema: 'public', table: 'couples', filter: `id=eq.${cId}` }, () => fetchCoupleData(cId, uid))
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${uid}` }, () => {
              console.log('[Realtime] Profile updated, refetching...');
              fetchCoupleData(cId, uid);
            })
            .subscribe((status) => console.log(`[Realtime] ${status}`));
        } else {
          await setupData(null, currentUser.id);
        }
        
        if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
        setIsLoading(false);
        console.log('[Init] Sequence completed successfully');
      } catch (err: any) {
        console.error('[Fatal] Init error:', err);
        if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
        setInitError(err.message || '알 수 없는 시스템 오류가 발생했습니다.');
        setIsLoading(false);
      }
    } else {
      console.log('[Auth] No session found');
      setCoupleId(null);
      setEntries([]);
      setBucketItems([]);
      setIsLoading(false);
    }
  }, [setupData, fetchEntries, fetchBucketItems, fetchCoupleData]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setupUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setupUser(session?.user ?? null);
    });

    // Mobile/Web Sync: Refetch on Focus or Visibility Change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        console.log('[Sync] App became visible. Refetching data...');
        setupData(coupleId, user.id);
      }
    };

    const handleFocus = () => {
      if (user) {
        console.log('[Sync] App focused. Refetching data...');
        setupData(coupleId, user.id);
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [setupUser, user, coupleId, setupData]);

  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadingMessage('안전하게 로그아웃 중입니다...');
      await supabase.auth.signOut();
      lastUserId.current = null;
      setUser(null);
      setCoupleId(null);
      setEntries([]);
      setBucketItems([]);
      setInitError(null);
      localStorage.removeItem('couple_info');
      localStorage.removeItem('dDayMode');
      localStorage.removeItem('timelineViewMode');
      sessionStorage.clear();
      console.log('[Auth] Signed out');
    } catch (error) {
      console.error('[Error] Sign out failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const withdraw = useCallback(async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      setLoadingMessage('모든 데이터를 삭제하고 탈퇴를 진행 중입니다...');

      const uid = user.id;
      const cId = coupleId;

      // 1. Delete user-created entries
      console.log('[Withdraw] Deleting timeline events...');
      await supabase.from('timeline_events').delete().eq('created_by', uid);

      // 2. Delete user-created bucket items
      console.log('[Withdraw] Deleting bucket list...');
      await supabase.from('bucket_list').delete().eq('created_by', uid);

      // 3. Delete couple record if solo
      if (cId === uid) {
        console.log('[Withdraw] Deleting solo couple record...');
        await supabase.from('couples').delete().eq('id', uid);
      }

      // 4. Delete profile
      console.log('[Withdraw] Deleting profile...');
      await supabase.from('profiles').delete().eq('id', uid);

      // 5. Sign out (This clears local session)
      console.log('[Withdraw] Signing out...');
      await signOut();
      
      alert('탈퇴가 완료되었습니다. 그동안 이용해주셔서 감사합니다.');
    } catch (error: any) {
      console.error('[Withdraw] Failed:', error);
      alert('탈퇴 처리 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [user, coupleId, signOut]);

  const linkCouple = async (partnerCode: string) => {
    if (!user) return;
    try {
      setIsLoading(true);
      setLoadingMessage('상대방과 연결을 진행하고 있습니다...');

      // 1. Fetch current manual data from profiles for migration
      const { data: myProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // 2. Perform the linking update
      const { error: linkError } = await supabase
        .from('profiles')
        .update({ couple_id: partnerCode })
        .eq('id', user.id);
      
      if (linkError) throw linkError;

      // 3. Migrate manual data to the new shared couples record if it exists
      if (myProfile && (myProfile.manual_partner_name || myProfile.manual_first_met_date || myProfile.manual_start_date)) {
        console.log('[Migration] Migrating manual data to couples table...');
        
        const { error: migrateError } = await supabase
          .from('couples')
          .update({
            name2: myProfile.manual_partner_name,
            first_met_date: myProfile.manual_first_met_date,
            anniversary_date: myProfile.manual_start_date,
          })
          .eq('id', partnerCode);

        if (!migrateError) {
          // Clear manual data after successful migration
          await supabase
            .from('profiles')
            .update({
              manual_partner_name: null,
              manual_first_met_date: null,
              manual_start_date: null,
            })
            .eq('id', user.id);
          console.log('[Migration] Success and cleared manual fields');
        }
      }

      alert('커플 연결이 완료되었습니다!');
      window.location.reload();
    } catch (error: any) {
      console.error('[Error] Linking process failed:', error);
      alert('연결에 실패했습니다: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const setCoupleInfo = async (info: CoupleInfo) => {
    console.log('[Settings] setCoupleInfo START', { 
      userId: user?.id, 
      coupleId: coupleId,
      isLinked: coupleId && coupleId !== user?.id,
      inputInfo: info 
    });
    
    if (!user) {
      console.error('[Settings] Save failed: No user session found');
      throw new Error('로그인 정보가 없습니다. 다시 로그인해 주세요.');
    }

    // Fallback to user.id if coupleId is somehow missing
    const targetCoupleId = coupleId || user.id;

    try {
      const isLinked = targetCoupleId !== user.id;
      
      if (isLinked) {
        // 1순위: 연결된 경우 couples 테이블 업데이트
        const updateData = {
          name1: info.name1,
          name2: info.name2,
          anniversary_date: info.anniversaryDate,
          first_met_date: info.firstMetDate,
          base_date_type: info.baseDateType,
        };
        console.log('[Settings] Updating shared couples table...', updateData);
        
        const { data, error, status } = await supabase
          .from('couples')
          .update(updateData)
          .eq('id', targetCoupleId)
          .select();

        console.log('[Settings] Couples update result:', { data, error, status });

        if (error) {
          console.error('[DB Error] Couples table update failed:', error);
          throw error;
        }
      } else {
        // 2순위: 혼자인 경우 profiles(manual_) 및 couples(own row) 업데이트
        const profileUpdateData = {
          manual_partner_name: info.name2,
          manual_first_met_date: info.firstMetDate,
          manual_start_date: info.anniversaryDate,
        };
        console.log('[Settings] Updating profiles table (solo mode)...', profileUpdateData);
        
        const profileResult = await supabase
          .from('profiles')
          .update(profileUpdateData)
          .eq('id', user.id)
          .select();

        console.log('[Settings] Profiles update result:', { 
          data: profileResult.data, 
          error: profileResult.error, 
          status: profileResult.status 
        });

        if (profileResult.error) {
          console.error('[DB Error] Profiles table update failed:', profileResult.error);
          throw profileResult.error;
        }

        const coupleSyncData = {
          name1: info.name1,
          name2: info.name2,
          anniversary_date: info.anniversaryDate,
          first_met_date: info.firstMetDate,
          base_date_type: info.baseDateType,
        };
        console.log('[Settings] Syncing to own couple record...', coupleSyncData);
        
        const coupleResult = await supabase
          .from('couples')
          .update(coupleSyncData)
          .eq('id', targetCoupleId)
          .select();

        console.log('[Settings] Own couple sync result:', { 
          data: coupleResult.data, 
          error: coupleResult.error, 
          status: coupleResult.status 
        });

        if (coupleResult.error) {
          console.warn('[DB Warning] Failed to sync to couple record, but profile was updated:', coupleResult.error);
        }
      }

      setCoupleInfoState(info);
      console.log('[Settings] Refreshing data from server...');
      await fetchCoupleData(targetCoupleId, user.id);
      console.log('[Settings] setCoupleInfo SUCCESS');
    } catch (error: any) {
      console.error('[Fatal] setCoupleInfo execution failed:', error);
      throw new Error(error.message || '저장 중 서버 오류가 발생했습니다.');
    }
  };

  const addEntry = async (newEntry: Omit<TimelineEntry, 'id' | 'meetingCount'>) => {
    if (!user) return;
    try {
      // If coupleId is null or matches user.id, we treat it as solo (null couple_id in DB)
      const effectiveCoupleId = (coupleId && coupleId !== user.id) ? coupleId : null;
      
      const entryData = { 
        ...newEntry, 
        created_by: user.id, 
        couple_id: effectiveCoupleId
      };
      
      console.log('[Timeline] Final Data to DB:', entryData);
      const { error } = await supabase.from('timeline_events').insert([entryData]);
      if (error) throw error;
      await fetchEntries(coupleId, user.id);
    } catch (error) {
      console.error('[Error] Add entry failed:', error);
      throw error;
    }
  };

  const updateEntry = async (id: string, updatedFields: Partial<TimelineEntry>) => {
    try {
      const { error } = await supabase.from('timeline_events').update(updatedFields).eq('id', id);
      if (error) throw error;
      await fetchEntries(coupleId, user?.id || '');
    } catch (error) {
      console.error('[Error] Update failed:', error);
      throw error;
    }
  };

  const deleteEntry = async (id: string) => {
    try {
      const { error } = await supabase.from('timeline_events').delete().eq('id', id);
      if (error) throw error;
      await fetchEntries(coupleId, user?.id || '');
    } catch (error) {
      console.error('[Error] Delete failed:', error);
      throw error;
    }
  };

  const entriesWithCounts = useMemo(() => getTimelineWithCounts(entries), [entries]);

  const addBucketItem = async (item: Omit<BucketItem, 'id'>) => {
    if (!user) return;
    try {
      const effectiveCoupleId = (coupleId && coupleId !== user.id) ? coupleId : null;
      
      const { error } = await supabase.from('bucket_list').insert([{
        title: item.title,
        description: item.description,
        category: item.category,
        is_completed: item.isCompleted,
        target_date: item.targetDate,
        created_by: user.id,
        couple_id: effectiveCoupleId
      }]);
      if (error) throw error;
      await fetchBucketItems(coupleId, user.id);
    } catch (error) {
      console.error('[Error] Add bucket item failed:', error);
      throw error;
    }
  };

  const updateBucketItem = async (id: string, updatedFields: Partial<BucketItem>) => {
    try {
      const { error } = await supabase.from('bucket_list').update({
        title: updatedFields.title,
        description: updatedFields.description,
        category: updatedFields.category,
        is_completed: updatedFields.isCompleted,
        target_date: updatedFields.targetDate,
      }).eq('id', id);
      if (error) throw error;
      await fetchBucketItems(coupleId, user?.id || '');
    } catch (error) {
      console.error('[Error] Update bucket item failed:', error);
      throw error;
    }
  };

  const toggleBucketItem = async (id: string) => {
    const item = bucketItems.find(i => i.id === id);
    if (item) await updateBucketItem(id, { isCompleted: !item.isCompleted });
  };

  return (
    <AppContext.Provider value={{ 
      user, coupleId, linkCouple, signOut, withdraw, coupleInfo, setCoupleInfo, 
      entries, addEntry, updateEntry, deleteEntry, entriesWithCounts,
      bucketItems, addBucketItem, updateBucketItem, toggleBucketItem,
      isLoading, loadingMessage, initError, dDayMode, setDDayMode
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within an AppProvider');
  return context;
};
